// ============================================================================
// Line OA — capability glue.
//
// Bridges the Line-specific send/profile helpers into the shape the
// capability registry expects (`AdapterMethods<'messaging'>`).
//
// The big call-out vs. the FB Messenger adapter is the REPLY vs. PUSH
// decision:
//
//   - If the caller supplies a fresh `replyToken` (via the optional
//     SendMessageArgs extension), AND we haven't already used it for this
//     correlationId, we send through `/v2/bot/message/reply` (FREE).
//   - Otherwise we send through `/v2/bot/message/push` (CHARGED).
//
// Reply tokens are single-use + 30s TTL. The adapter cannot observe
// per-conversation state (the registry is stateless), so we rely on the
// caller (chat module / AI agent) to clear the token after a successful
// reply — re-using the same token yields `INVALID_REPLY_TOKEN` from Line.
// ============================================================================

import type { AdapterMethods } from '../../services/capability-registry'
import type {
  MessageAttachment,
  SendMessageArgs,
  SendMessageResult,
} from '../../capabilities/messaging'
import { getLineUserProfile } from './profile'
import {
  buildLineTextMessage,
  buildMessagesFromAttachments,
  buildLineQuickReply,
  sendLinePush,
  sendLineReply,
  type LineMessage,
  type LineQuickReply,
} from './send'

// ============================================================================
// Dependencies
// ============================================================================

export interface LineOaMessagingMethodsDeps {
  /**
   * Injectable fetch for tests. Defaults to the global `fetch`.
   */
  fetchImpl?: typeof fetch
  /** Optional structured logger. Defaults to no-op. */
  logger?: {
    warn?: (msg: string, meta?: Record<string, unknown>) => void
    debug?: (msg: string, meta?: Record<string, unknown>) => void
  }
}

/**
 * Line-specific extension of the capability-level `SendMessageArgs`. The
 * canonical shape has no `replyToken`; we accept it via an extra property
 * that TypeScript keeps non-structural (no re-declaration of the base type).
 */
export type LineSendMessageArgs = SendMessageArgs & {
  /**
   * Fresh reply token from the most recent inbound user event. If present,
   * the adapter sends through /reply (free) instead of /push (charged).
   */
  replyToken?: string
}

// ============================================================================
// buildLineOaMessagingMethods
// ============================================================================

export function buildLineOaMessagingMethods(
  deps: LineOaMessagingMethodsDeps = {},
): AdapterMethods<'messaging'> {
  const fetchImpl = deps.fetchImpl ?? fetch
  const logger = deps.logger ?? {}

  return {
    async sendMessage(args, ctx): Promise<SendMessageResult> {
      const lineArgs = args as LineSendMessageArgs
      const replyToken = typeof lineArgs.replyToken === 'string' && lineArgs.replyToken.length > 0
        ? lineArgs.replyToken
        : undefined

      const messages = buildLineMessagesFromCapabilityArgs({
        text: args.text,
        attachments: args.attachments,
      })

      if (messages.length === 0) {
        // No text + no attachments → nothing meaningful to send. We return a
        // synthetic no-op result rather than throwing so callers treating
        // this as a best-effort relay don't blow up; the dispatcher can
        // detect the empty id and skip ack.
        return { providerMessageId: '', sentAt: new Date().toISOString() }
      }

      if (replyToken) {
        await sendLineReply(replyToken, messages, ctx.accessToken, fetchImpl)
      } else {
        await sendLinePush(args.to, messages, ctx.accessToken, fetchImpl)
      }
      const sentAt = new Date().toISOString()

      // Line's reply/push endpoints return `{}` on success — there is NO
      // provider-side message id for outbound. We synthesise one from the
      // correlationId (or ctx.instanceId + now + random) so downstream dedupe
      // has a stable handle. Delivery / read receipts never arrive for
      // outbound Line messages either, so this synthesised id is only ever
      // used inside Kubdee.
      //
      // `crypto.randomUUID()` is CSPRNG-backed (available in Workers + modern
      // Node), unlike `Math.random()` which can collide on two concurrent
      // sends in the same millisecond on the same instance. (Round 1 M1 fix.)
      const providerMessageId =
        args.correlationId ||
        `line-${ctx.instanceId}-${Date.now()}-${crypto.randomUUID().slice(0, 8)}`

      return { providerMessageId, sentAt }
    },

    async sendTyping(_args, _ctx): Promise<void> {
      // Line has no typing indicator API for bots / OAs. Callers still get a
      // resolved promise so code written against the capability contract
      // keeps working. We log once per call at `warn` so operators notice
      // repeated attempts in the dashboard.
      if (typeof logger.warn === 'function') {
        logger.warn('[connectors/line-oa] sendTyping is a no-op (Line has no bot typing API)')
      }
    },

    async fetchUserProfile(args, ctx) {
      return getLineUserProfile({ userId: args.userId }, ctx.accessToken, fetchImpl)
    },
  }
}

// ============================================================================
// Internal — build the Line message array from capability-level args
// ============================================================================

function buildLineMessagesFromCapabilityArgs(args: {
  text?: string
  attachments?: MessageAttachment[]
}): LineMessage[] {
  const out: LineMessage[] = []

  // 1. Text message first (if any) — becomes the anchor for a quickReply.
  if (typeof args.text === 'string' && args.text.length > 0) {
    out.push(buildLineTextMessage(args.text))
  }

  // 2. Attachments mapped to Line types. Quick-reply attachments mutate the
  //    LAST prior message (text or media); if no prior exists we fall back
  //    to a single-emoji placeholder text so the quickReply still renders.
  if (Array.isArray(args.attachments) && args.attachments.length > 0) {
    // Separate quick_replies from media so we can post media first and attach
    // the quickReply to the tail in a predictable order.
    const mediaAttachments = args.attachments.filter(
      (a): a is Exclude<MessageAttachment, { type: 'quick_replies' }> =>
        a.type !== 'quick_replies',
    )
    const quickRepliesAttachment = args.attachments.find(
      (a): a is Extract<MessageAttachment, { type: 'quick_replies' }> =>
        a.type === 'quick_replies',
    )

    const mediaMessages = buildMessagesFromAttachments(mediaAttachments)
    for (const m of mediaMessages) out.push(m)

    if (quickRepliesAttachment) {
      const qr: LineQuickReply = buildLineQuickReply(quickRepliesAttachment.options)
      if (out.length > 0) {
        const last = out[out.length - 1] as LineMessage & { quickReply?: LineQuickReply }
        last.quickReply = qr
      } else {
        // No prior message — add a tiny placeholder text carrying the
        // quickReply. Line requires quickReply to live on a message object.
        out.push({ type: 'text', text: '​', quickReply: qr })
      }
    }
  }

  return out
}
